import { useId, useState } from 'react'
import { useLocation, useNavigation, useSearchParams } from 'react-router'
import Loader from '@/site/components/Loader'
import NotFound from '@/site/components/NotFound'
import SearchFilters, { ActiveFilters } from '@/site/components/SearchFilters'
import SearchPagination from '@/site/components/SearchPagination'
import TutorialCard from '@/site/components/TutorialCard'
import { formatCount } from '@/site/lib/format'
import { applyFilter, removeFilter, searchParamsFromFilters, sortOptions, type SearchFilters as Filters } from '@/site/lib/search'
import { searchStateFromParams, type SearchResults } from '@/site/lib/searchPage'
import { hitKey } from '@/site/lib/tutorialHit'

interface Props {
  /** The route loader's results for the current URL. */
  results: SearchResults
  /** The page's <h1>; false when the page already has one above (collection pages). */
  heading?: string | false
}

const toSearch = (filters: Filters) => {
  const params = searchParamsFromFilters(filters).toString()
  return params ? `?${params}` : ''
}

/**
 * Tutorial listing (/tutorials, /:filter/:value and collection pages). Results come from the route loader;
 * every filter, sort and page is part of the URL, so back/forward and shared links restore the same results.
 */
export default function SearchView({ results, heading }: Props) {
  const location = useLocation()
  const navigation = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const sortId = useId()

  const { filters, query, sort } = searchStateFromParams(searchParams)
  const options = sortOptions(query)
  // Only a change of this listing's query shows the loader (not navigating away to another page).
  const isFetching = navigation.state === 'loading' && navigation.location.pathname === location.pathname

  const selectHref = (field: string, value: string) => toSearch(applyFilter(filters, field, value))
  const removeHref = (field: string, value: string) => toSearch(removeFilter(filters, field, value))
  const pageHref = (page: number) => toSearch(applyFilter(filters, 'page', String(page)))

  const title = heading === false ? null : heading || (query ? `Results for “${query}”` : 'All tutorials')
  const activeCount = Object.keys(filters).filter((k) => !['q', 'page', 'sort'].includes(k)).length

  const filterPanel = results.facets.length > 0 && (
    <div className="rounded-3xl bg-qt-soft p-2">
      <div className="qt-stitch rounded-[18px] p-4">
        <h2 className="sr-only">Filter tutorials</h2>
        <SearchFilters searchFilters={filters} facets={results.facets} selectHref={selectHref} removeHref={removeHref} />
      </div>
    </div>
  )

  return (
    <div className="qt-container mt-6 md:mt-9">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          {title && <h1 className="text-3xl font-extrabold text-qt-ink md:text-[40px]">{title}</h1>}
          <p className="mt-1 text-sm font-bold text-qt-muted" aria-live="polite">
            {formatCount(results.totalResults)} {results.totalResults === 1 ? 'tutorial' : 'tutorials'}
          </p>
        </div>
        {results.hits.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor={sortId} className="text-sm font-bold">
              Sort by
            </label>
            <select
              id={sortId}
              name="sort"
              value={sort}
              onChange={(e) => setSearchParams(searchParamsFromFilters(applyFilter(filters, 'sort', e.target.value)), { preventScrollReset: true })}
              className="h-11 rounded-xl border-2 border-qt-line py-0 pr-9 pl-3 text-sm font-bold focus:border-qt-primary focus:ring-0"
            >
              {options.map((o) => (
                <option value={o.field} key={o.field}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[290px_minmax(0,1fr)] lg:gap-10">
        <aside aria-label="Filters" className="lg:self-start">
          <button
            type="button"
            aria-expanded={filtersOpen}
            aria-controls="search-filters"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-qt-text text-sm font-bold lg:hidden"
          >
            {filtersOpen ? 'Hide filters' : `Filter tutorials${activeCount ? ` (${activeCount})` : ''}`}
          </button>
          <div id="search-filters" className={`${filtersOpen ? 'mt-4 block' : 'hidden'} lg:mt-0 lg:block`}>
            {filterPanel}
          </div>
        </aside>

        <div className="flex min-w-0 flex-col gap-5">
          <ActiveFilters searchFilters={filters} removeHref={removeHref} />

          {isFetching && <Loader />}

          {!isFetching && results.hits.length === 0 && <NotFound message="No tutorials match these filters." />}

          {results.hits.length > 0 && (
            <div className={`grid grid-cols-2 gap-x-4 gap-y-7 md:grid-cols-3 md:gap-6 ${isFetching ? 'opacity-50' : ''}`}>
              {results.hits.map((hit, i) => (
                <TutorialCard key={hitKey(hit, i)} hit={hit} headingLevel={title ? 'h2' : 'h3'} />
              ))}
            </div>
          )}

          {results.totalPages > 1 && <SearchPagination currentPage={results.currentPage} totalPages={results.totalPages} pageHref={pageHref} />}
        </div>
      </div>
    </div>
  )
}
